import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "@/components/ui/field";
import { MultiSelect, type MultiOption } from "@/components/ui/multi-select";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Clause, Question } from "@/lib/intake-flow";
import { cn } from "@/lib/utils";

/**
 * When a branch is taken (ACL-338).
 *
 * A branch's conditions are rows joined by "and"; an "or" is a second branch. Every row compares
 * a stored option key, never the words a rep sees: the label is the admin's to rename and the
 * console's to translate, and a rule written against the words breaks the moment either changes.
 * The picker therefore offers the question's options and sends their keys.
 */

/** What each kind of answer may be asked, which the engine enforces too. */
const OPS: Record<string, { op: string; label: string; takesValue: boolean }[]> = {
  dropdown: [
    { op: "eq", label: "is", takesValue: true },
    { op: "ne", label: "is not", takesValue: true },
    { op: "in", label: "is one of", takesValue: true },
    { op: "not_in", label: "is none of", takesValue: true },
    { op: "not_empty", label: "was answered", takesValue: false },
    { op: "empty", label: "was not answered", takesValue: false },
  ],
  boolean: [
    { op: "eq", label: "is", takesValue: true },
    { op: "not_empty", label: "was answered", takesValue: false },
    { op: "empty", label: "was not answered", takesValue: false },
  ],
  multi_select: [
    { op: "contains", label: "includes", takesValue: true },
    { op: "contains_any", label: "includes any of", takesValue: true },
    { op: "not_empty", label: "was answered", takesValue: false },
    { op: "empty", label: "was not answered", takesValue: false },
  ],
  number: [
    { op: "eq", label: "is", takesValue: true },
    { op: "ne", label: "is not", takesValue: true },
    { op: "lt", label: "is under", takesValue: true },
    { op: "gt", label: "is over", takesValue: true },
    { op: "not_empty", label: "was answered", takesValue: false },
    { op: "empty", label: "was not answered", takesValue: false },
  ],
};

const ASKED_ONLY = [
  { op: "not_empty", label: "was answered", takesValue: false },
  { op: "empty", label: "was not answered", takesValue: false },
];

function opsFor(type: string | undefined) {
  return (type && OPS[type]) || ASKED_ONLY;
}

/**
 * How a condition joins the one before it.
 *
 * `and` binds tighter than `or`, so the rows read as a run of or-separated groups. The sentence
 * under the picker says which group this row falls into, because reading precedence off a
 * stack of dropdowns is exactly the thing people get wrong.
 */
function JoinPicker({
  value,
  onChange,
}: {
  value: "and" | "or";
  onChange: (next: "and" | "or") => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <div className="inline-flex overflow-hidden rounded-md border border-border">
        {(["and", "or"] as const).map((one) => (
          <button
            key={one}
            type="button"
            onClick={() => onChange(one)}
            aria-pressed={value === one}
            className={cn(
              "px-2.5 py-1 text-xs font-medium",
              value === one
                ? "bg-accent-3 text-accent-11"
                : "bg-surface text-text-muted hover:bg-surface-hover",
            )}
          >
            {one === "and" ? "and" : "or"}
          </button>
        ))}
      </div>
      <span className="text-xs text-text-muted">
        {value === "and" ? "both must hold" : "either is enough"}
      </span>
    </div>
  );
}

export function ConditionDialog({
  open,
  onOpenChange,
  label,
  conditions,
  fallback = false,
  questions,
  /** The questions asked before this branch, which are the only ones it can read. */
  askedBefore,
  firms = [],
  campaigns = [],
  onSave,
  onUndoSplit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  label: string | null;
  conditions: Clause[];
  /** The last path out of a step, which always matches and so carries no conditions. */
  fallback?: boolean;
  questions: Question[];
  askedBefore: string[];
  /** The tenant's firms and campaigns, so a rule on one shows its name rather than a bare id. */
  firms?: { id: number; label?: string | null; name: string }[];
  campaigns?: { id: string; name: string }[];
  onSave: (next: { label: string | null; conditions: Clause[] }) => void;
  /** Take the whole split out, leaving the step that made it with one way on. */
  onUndoSplit?: () => void;
}) {
  const [rows, setRows] = React.useState<Clause[]>([]);
  const [name, setName] = React.useState("");

  React.useEffect(() => {
    if (!open) return;
    setRows(conditions.length ? conditions : [{ source: "answer", op: "eq" }]);
    setName(label ?? "");
  }, [open, conditions, label]);

  const available = questions.filter((q) => askedBefore.includes(q.key));

  function setRow(index: number, patch: Partial<Clause>) {
    setRows((old) => old.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function remove(index: number) {
    setRows((old) => old.filter((_, i) => i !== index));
  }

  // A path with no conditions would match everyone and shadow the real last path, so an empty
  // list is as unfinished as a half-filled row. The last path itself is the one exception.
  const incomplete = (!fallback && rows.length === 0) || rows.some((row) => {
    // A firm or campaign rule needs the one it names: without a value it would never hold, so
    // no lead would ever take the path.
    if (row.source === "firm" || row.source === "campaign") {
      return !row.op || row.value === undefined || row.value === "";
    }
    if (row.source !== "answer") return !row.op;
    if (!row.field) return true;
    const spec = opsFor(questions.find((q) => q.key === row.field)?.type).find((o) => o.op === row.op);
    if (!spec) return true;
    return spec.takesValue && (row.value === undefined || row.value === "");
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DialogHeader>
          <DialogTitle>{fallback ? "The last path" : "When this branch is taken"}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field
            label="What to call this branch"
            htmlFor="branch-label"
            hint="What the admin sees on the canvas, for example Male or Texas."
          >
            <Input id="branch-label" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>

          {fallback ? (
            <div className="space-y-2 rounded-lg border border-border bg-surface-sunken/50 p-3">
              <p className="text-sm text-text-muted">
                This path takes everyone the paths above it did not name, so it has no conditions
                of its own: the last path always matches, and that is what stops a lead reaching a
                question with nowhere to go. To ask this group something different, give the paths
                above it their own conditions, or add another path.
              </p>
              {onUndoSplit ? (
                // The sentence used to end "delete the step that made them" and leave the admin
                // to find it. The action it describes belongs here, where they are reading it.
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    onUndoSplit();
                    onOpenChange(false);
                  }}
                >
                  Remove every path on this step
                </Button>
              ) : null}
            </div>
          ) : null}

          <div className={fallback ? "hidden" : "space-y-3"}>
            {rows.map((row, index) => {
              const question = questions.find((q) => q.key === row.field);
              const specs = opsFor(question?.type);
              const spec = specs.find((o) => o.op === row.op);

              // A rule on the firm or the campaign is not a question, so it gets the picker for
              // what it actually names. Rendering it as a question dropdown showed an empty
              // picker and rewrote the rule to `answer` the moment it was touched, which is how
              // a path made by Vary by lost the firm it was for.
              if (row.source === "firm" || row.source === "campaign") {
                const options =
                  row.source === "firm"
                    ? firms.map((one) => ({ value: String(one.id), label: one.label ?? one.name }))
                    : campaigns.map((one) => ({ value: String(one.id), label: one.name }));
                return (
                  <div
                    key={index}
                    className="space-y-2 rounded-lg border border-border bg-surface-sunken/60 p-3"
                  >
                    {index > 0 ? (
                      <JoinPicker
                        value={row.join === "or" ? "or" : "and"}
                        onChange={(next) => setRow(index, { join: next })}
                      />
                    ) : null}
                    <p className="text-xs font-medium text-text">
                      {row.source === "firm" ? "The firm is" : "The campaign is"}
                    </p>
                    <Select
                      value={String(row.value ?? "")}
                      onValueChange={(value) =>
                        setRow(index, {
                          ...row,
                          op: "eq",
                          // A firm id is a number on the wire, a campaign id a string.
                          value: row.source === "firm" ? Number(value) : value,
                        })
                      }
                    >
                      <SelectTrigger
                        aria-label={row.source === "firm" ? "Which firm" : "Which campaign"}
                      >
                        <SelectValue placeholder={`Pick a ${row.source}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {options.map((one) => (
                          <SelectItem key={one.value} value={one.value}>
                            {one.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="sm" onClick={() => remove(index)}>
                      Remove this rule
                    </Button>
                  </div>
                );
              }

              return (
                <div key={index} className="space-y-2 rounded-lg border border-border bg-surface-sunken/60 p-3">
                  {index > 0 ? (
                    <JoinPicker
                      value={row.join === "or" ? "or" : "and"}
                      onChange={(next) => setRow(index, { join: next })}
                    />
                  ) : null}
                  <Select
                    value={row.field ?? ""}
                    onValueChange={(value) =>
                      setRow(index, {
                        source: "answer",
                        field: value,
                        op: "eq",
                        value: undefined,
                        // Changing the question must not silently re-join the row.
                        join: row.join,
                      })
                    }
                  >
                    <SelectTrigger aria-label="The question this reads">
                      <SelectValue placeholder="Pick a question" />
                    </SelectTrigger>
                    <SelectContent>
                      {available.map((q) => (
                        <SelectItem key={q.key} value={q.key}>
                          {q.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={row.op} onValueChange={(value) => setRow(index, { op: value, value: undefined })}>
                    <SelectTrigger aria-label="How it compares">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {specs.map((o) => (
                        <SelectItem key={o.op} value={o.op}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {spec?.takesValue ? (
                    <ValuePicker
                      question={question}
                      row={row}
                      firms={firms}
                      campaigns={campaigns}
                      onChange={(v) => setRow(index, { value: v })}
                    />
                  ) : null}

                  {/* Removable whatever the count: a branch down to no conditions is one the
                      admin is still writing, and Save is what refuses an unfinished one. */}
                  <Button variant="ghost" size="sm" onClick={() => remove(index)}>
                    Remove this condition
                  </Button>
                </div>
              );
            })}
            {/* Their own row: as children of `space-y-3` they were spaced apart vertically and
                sat flush against each other once they wrapped onto one line. */}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setRows((old) => [...old, { source: "answer", op: "eq" }])}
              >
                Add a condition
              </Button>
              {firms.length ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRows((old) => [...old, { source: "firm", op: "eq" }])}
                >
                  Add a firm rule
                </Button>
              ) : null}
              {campaigns.length ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRows((old) => [...old, { source: "campaign", op: "eq" }])}
                >
                  Add a campaign rule
                </Button>
              ) : null}
            </div>
          </div>

          {!fallback && rows.length === 0 ? (
            <p className="text-xs text-danger-11">
              A path with no conditions would take everyone, which is what the last path is for.
              Add a condition, or remove this path from the canvas.
            </p>
          ) : null}
          {available.length === 0 && !fallback ? (
            <p className="text-xs text-text-muted">
              No question is asked before this branch yet, so there is nothing it can read. Add a
              question above it first.
            </p>
          ) : null}
          {fallback ? null : (
            <p className="text-xs text-text-muted">
              Conditions compare the answer's stored value, so renaming what a rep sees never
              changes which branch a lead takes.
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!fallback && incomplete}
            onClick={() => {
              // A Fallback keeps its empty conditions whatever the rows say: that is what makes
              // it the catch-all.
              onSave({ label: name.trim() || null, conditions: fallback ? [] : rows });
              onOpenChange(false);
            }}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The value a condition compares: the question's own options wherever it has them. */
function ValuePicker({
  question,
  row,
  firms,
  campaigns,
  onChange,
}: {
  question: Question | undefined;
  row: Clause;
  /** The same narrow shapes the dialog itself takes: a name to show and the id the rule holds. */
  firms: { id: number; label?: string | null; name: string }[];
  campaigns: { id: string; name: string }[];
  onChange: (value: unknown) => void;
}) {
  const many = row.op === "in" || row.op === "not_in" || row.op === "contains_any";

  // A firm or a campaign is picked from the tenant's own, never typed. The rule stores an id, so
  // typing one by hand meant reading it off another screen and a slip wrote a rule that silently
  // matched nobody — and the canvas then read "the firm is 7".
  if (row.source === "firm" || row.source === "campaign") {
    const options: MultiOption[] =
      row.source === "firm"
        ? firms.map((f) => ({
            value: String(f.id),
            label: f.label ?? f.name,
            keywords: f.name,
          }))
        : campaigns.map((c) => ({ value: c.id, label: c.name }));
    // An id is a number for a firm and a string for a campaign, which is what the engine compares.
    const out = (id: string) => (row.source === "firm" ? Number(id) : id);

    if (many) {
      const chosen = (Array.isArray(row.value) ? row.value : []).map(String);
      return (
        <MultiSelect
          label={row.source === "firm" ? "Firms" : "Campaigns"}
          options={options}
          value={chosen}
          onChange={(next) => onChange(next.map(out))}
          placeholder={row.source === "firm" ? "Pick the firms" : "Pick the campaigns"}
        />
      );
    }
    const one = row.value === null || row.value === undefined ? "" : String(row.value);
    return (
      <Select value={one} onValueChange={(v) => onChange(out(v))}>
        <SelectTrigger aria-label={row.source === "firm" ? "The firm" : "The campaign"}>
          <SelectValue placeholder={row.source === "firm" ? "Pick a firm" : "Pick a campaign"} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (question?.type === "boolean") {
    return (
      <Select value={row.value === true ? "true" : row.value === false ? "false" : ""} onValueChange={(v) => onChange(v === "true")}>
        <SelectTrigger aria-label="The answer">
          <SelectValue placeholder="Pick an answer" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="true">Yes</SelectItem>
          <SelectItem value="false">No</SelectItem>
        </SelectContent>
      </Select>
    );
  }

  if (question?.options?.length) {
    if (many) {
      const chosen = Array.isArray(row.value) ? (row.value as string[]) : [];
      return (
        <div className="space-y-1">
          {question.options.map((option) => (
            <label key={option.key} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={chosen.includes(option.key)}
                onChange={(e) =>
                  onChange(
                    e.target.checked
                      ? [...chosen, option.key]
                      : chosen.filter((k) => k !== option.key),
                  )
                }
              />
              {option.label}
            </label>
          ))}
        </div>
      );
    }
    return (
      <Select value={typeof row.value === "string" ? row.value : ""} onValueChange={onChange}>
        <SelectTrigger aria-label="The answer">
          <SelectValue placeholder="Pick an answer" />
        </SelectTrigger>
        <SelectContent>
          {question.options.map((option) => (
            <SelectItem key={option.key} value={option.key}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <Input
      value={typeof row.value === "string" || typeof row.value === "number" ? String(row.value) : ""}
      onChange={(e) => onChange(question?.type === "number" ? Number(e.target.value) : e.target.value)}
      placeholder="The value to compare"
    />
  );
}
